import { Directive, effect, ElementRef, inject, input } from '@angular/core';
import { Dialog } from 'primeng/dialog';

function truncate( obj: any ) {
    if ( obj instanceof HTMLElement ) {
        return `<${obj.tagName.toLowerCase()} id="${obj.id}" class="${obj.className}">`;
    }
    return String( obj ).slice( 0, 50 ) + ( String( obj ).length > 50 ? '...' : '' );
}

/**
 * Directive that restores focus to the host element when a bound boolean expression changes from `true` to `false`,
 * or when a bound `p-dialog` instance is hidden. This is useful for maintaining accessibility and user experience
 * by returning focus to the element that triggered an overlay or dialog.
 */
@Directive({
    exportAs: 'hisRestoreFocus',
    selector: '[hisRestoreFocus], [hisRestoreFocusOnHide]',
    standalone: false
})
export class HisRestoreFocusDirective {
    private readonly hostElement = inject( ElementRef );
    
    /**
     * Boolean binding that restores focus to the host element. Focus occurs when going from `true` to `false`,
     * following the semantic of switching from 'visible' to 'not visible', i.e. when closing a `p-dialog`.
     * @example```html
     * <button [hisRestoreFocus]="showDialog" (click)="showDialog = true">Open Dialog</button>
     * <p-dialog [(visible)]="showDialog">
     * ```
     */
    readonly hisRestoreFocus = input<boolean>( undefined );
    private readonly onVisibleEffect = effect( () => {
        const visible = this.hisRestoreFocus() === true;
        if ( this.lastVisible && !visible ) {
            this.focusHost();
        }
        this.lastVisible = visible;
    } );

    /**
     * Binds to the provided `p-dialog` instance and restores focus to the host element when the dialog is hidden.
     * @example```html
     * <button [hisRestoreFocusOnHide]="dialog" (click)="showDialog = true">Open Dialog</button>
     * <p-dialog #dialog [(visible)]="showDialog">
     * ```
     */
    readonly hisRestoreFocusOnHide = input<Dialog>( undefined );
    private readonly onHideEffect = effect( ( onCleanup ) => {
        const dialog = this.hisRestoreFocusOnHide();
        if ( dialog ) {
            this.lastOnHideVisible = true;
            const subscription = dialog.onHide.subscribe( () => this.focusHost() );
            onCleanup( () => subscription.unsubscribe() );
            return;
        }
        if ( this.lastOnHideVisible ) {
            this.lastOnHideVisible = false;
            this.focusHost();
        }
    } );

    /** Tracks the last known visibility state bound to `hisRestoreFocus` */
    private lastVisible = false;
    /** Tracks the last known visibility state of the bound dialog */
    private lastOnHideVisible = false;

    private focusHost() {
        setTimeout( () => {
            const el = this.hostElement?.nativeElement;
            if ( el && typeof el.focus === 'function' ) {
                el.focus();
            }
        }, 0 );
    }
}